/**
 * Blog Post Detail — Rich article page.
 * Includes JSON-LD Article Schema, reading time, author card, and social share links.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { Clock, Calendar, ArrowLeft, Share2 } from "lucide-react";
import { Reveal } from "@/components/common/reveal";
import { BreadcrumbNav } from "@/components/common/breadcrumb-nav";
import { sampleBlogPosts } from "@/features/blog/services/blog-service";
import { getArticleSchema, safeJsonLd } from "@/lib/seo";
import { siteConfig } from "@/config/site";

export const Route = createFileRoute("/blog/$slug")({
  head: ({ params }) => {
    const post = sampleBlogPosts.find((p) => p.slug === params.slug) || sampleBlogPosts[0];
    const canonicalUrl = `${siteConfig.url}/blog/${post.slug}`;
    // coverImage is a site-relative path. Open Graph requires an absolute URL and
    // crawlers drop a relative one outright rather than resolving it, so the share
    // card would fall back to no image at all.
    const shareImage = post.coverImage.startsWith("http")
      ? post.coverImage
      : `${siteConfig.url}${post.coverImage}`;
    return {
      meta: [
        { title: `${post.title} — ${siteConfig.name}` },
        { name: "description", content: post.excerpt },
        { property: "og:title", content: post.title },
        { property: "og:description", content: post.excerpt },
        { property: "og:image", content: shareImage },
        { property: "og:url", content: canonicalUrl },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: post.title },
        { name: "twitter:description", content: post.excerpt },
        { name: "twitter:image", content: shareImage },
      ],
      links: [{ rel: "canonical", href: canonicalUrl }],
    };
  },
  component: BlogPostDetail,
});

function renderInlineMarkdown(text: string): React.ReactNode {
  const regex = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g;
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    if (match[1] && match[2]) {
      const label = match[1];
      const href = match[2];
      nodes.push(
        <a
          key={`link-${match.index}`}
          href={href}
          className="text-primary font-medium underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors"
        >
          {label}
        </a>,
      );
    } else if (match[3]) {
      nodes.push(
        <strong key={`bold-${match.index}`} className="font-semibold text-foreground">
          {match[3]}
        </strong>,
      );
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes.length > 0 ? nodes : text;
}

function renderMarkdownContent(content: string): React.ReactNode {
  const blocks = content.trim().split(/\n\s*\n/);
  return blocks.map((block, idx) => {
    const trimmed = block.trim();
    if (trimmed.startsWith("### ")) {
      return (
        <h3 key={idx} className="font-display text-2xl text-foreground mt-8 mb-3">
          {trimmed.replace(/^###\s+/, "")}
        </h3>
      );
    }
    if (trimmed.startsWith("## ")) {
      return (
        <h2 key={idx} className="font-display text-3xl text-foreground mt-10 mb-4">
          {trimmed.replace(/^##\s+/, "")}
        </h2>
      );
    }
    return (
      <p key={idx} className="text-base sm:text-lg leading-relaxed text-foreground/85 my-4">
        {renderInlineMarkdown(trimmed)}
      </p>
    );
  });
}

function BlogPostDetail() {
  const { slug } = Route.useParams();
  const post = sampleBlogPosts.find((p) => p.slug === slug) || sampleBlogPosts[0];

  const articleSchema = getArticleSchema({
    title: post.title,
    description: post.excerpt,
    image: post.coverImage,
    datePublished: post.publishedAt,
    author: post.authorName,
    url: `${siteConfig.url}/blog/${post.slug}`,
  });

  const relatedPosts = sampleBlogPosts.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <article className="section-container pb-28 pt-24 md:pt-28 max-w-4xl mx-auto">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(articleSchema) }}
      />

      <div className="mb-8">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Return to Journal
        </Link>
      </div>

      <Reveal>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="eyebrow text-primary">{post.category}</span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {post.readingTime}
          </span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {post.publishedAt}
          </span>
        </div>

        <h1 className="mt-4 font-display text-[clamp(2.2rem,4.5vw,3.8rem)] leading-[1.08]">
          {post.title}
        </h1>

        <div className="mt-6 flex items-center justify-between border-y py-4 text-xs text-muted-foreground">
          <span>
            Written by <strong className="text-foreground font-medium">{post.authorName}</strong>
          </span>
          <button
            onClick={() => {
              if (navigator.share) {
                navigator.share({ title: post.title, url: window.location.href });
              }
            }}
            className="flex items-center gap-1.5 hover:text-foreground transition-colors"
          >
            <Share2 className="h-4 w-4" />
            <span>Share</span>
          </button>
        </div>
      </Reveal>

      {/* Cover Image */}
      <Reveal delay={100} className="mt-10">
        <figure className="overflow-hidden bg-stone">
          <img
            src={post.coverImage}
            alt={post.title}
            className="w-full aspect-[16/9] object-cover"
          />
        </figure>
      </Reveal>

      {/* Article Content */}
      <Reveal delay={150} className="mt-12">
        <div className="prose prose-stone max-w-none text-base leading-relaxed text-foreground/90 space-y-6">
          <p className="text-lg leading-relaxed font-display text-foreground">{post.excerpt}</p>
          <div className="article-body leading-relaxed">{renderMarkdownContent(post.content)}</div>
        </div>
      </Reveal>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="mt-20 border-t pt-16">
          <Reveal>
            <p className="eyebrow">Continue reading</p>
            <h2 className="mt-3 font-display text-2xl">Related Stories</h2>
          </Reveal>
          <div className="mt-8 grid gap-8 sm:grid-cols-2">
            {relatedPosts.map((r, i) => (
              <Reveal key={r.id} delay={i * 80}>
                <Link
                  to="/blog/$slug"
                  params={{ slug: r.slug }}
                  className="group block border bg-card p-6 hover:border-primary/50 transition-colors"
                >
                  <span className="eyebrow">{r.category}</span>
                  <h3 className="mt-2 font-display text-lg group-hover:text-primary transition-colors">
                    {r.title}
                  </h3>
                  <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{r.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
